import { FeatureTableOptions } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

import { GeckoWeld } from './geckoWelds'

export interface GeckoConeCircumferentialWeldJSON {
  name: string | undefined
  type: GeckoFeatureModelType.ConeCircumferentialWeld
  feature_geometry: {
    x_start: number
    x_end: number
    y: number
  }
}

export const NewGeckoConeCircumferentialWeldJSON: GeckoConeCircumferentialWeldJSON =
  {
    name: 'New Cone Circumferential Weld',
    type: GeckoFeatureModelType.ConeCircumferentialWeld,
    feature_geometry: {
      x_start: 0,
      x_end: 0,
      y: 0,
    },
  }

export class GeckoConeCircumferentialWeld extends GeckoWeld {
  public readonly type = GeckoFeatureModelType.ConeCircumferentialWeld

  private constructor(
    componentID: string,
    weldID: string,
    weldJson: GeckoConeCircumferentialWeldJSON
  ) {
    const name = parseString(weldJson.name)
    const feature_geometry = weldJson.feature_geometry as Record<string, number>
    super(
      componentID,
      weldID,
      name,
      feature_geometry.x_start as number,
      feature_geometry.y as number,
      feature_geometry.x_end as number,
      feature_geometry.y as number
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJson: Record<string, GeckoConeCircumferentialWeldJSON>
  ): Map<string, GeckoConeCircumferentialWeld> {
    return Object.keys(recordsJson).reduce((resultObj, weldID) => {
      resultObj.set(
        weldID,
        new GeckoConeCircumferentialWeld(
          componentID,
          weldID,
          recordsJson[weldID]
        )
      )
      return resultObj
    }, new Map<string, GeckoConeCircumferentialWeld>())
  }

  public static CreateFromJSON(
    componentID: string,
    weldID: string,
    weldJson: GeckoConeCircumferentialWeldJSON
  ): GeckoConeCircumferentialWeld {
    return new GeckoConeCircumferentialWeld(componentID, weldID, weldJson)
  }

  public asJSON() {
    return generateConeCircumferentialWeld(
      this.name,
      this.feature_geometry.y.min,
      this.feature_geometry.x.min,
      this.feature_geometry.x.max
    )
  }
}

export function generateConeCircumferentialWeld(
  name: string | undefined,
  y: GeckoMeasurement,
  xStart: GeckoMeasurement,
  xEnd: GeckoMeasurement
): GeckoConeCircumferentialWeldJSON {
  const coneCircumferentialWeldJSON: GeckoConeCircumferentialWeldJSON = {
    name: name ?? undefined,
    type: GeckoFeatureModelType.ConeCircumferentialWeld,
    feature_geometry: {
      y: y
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      x_start: xStart
        .convertTo(GeckoMeasurementUnit.Degrees)
        .toPrecision(assetModelPrecision).num,
      x_end: xEnd
        .convertTo(GeckoMeasurementUnit.Degrees)
        .toPrecision(assetModelPrecision).num,
    },
  }

  return coneCircumferentialWeldJSON
}

export const ConeCircumferentialWeldFormFields = (
  weldJSON: GeckoConeCircumferentialWeldJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Name',
      value: weldJSON.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: weldJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_start',
      label: 'X Start',
      value: weldJSON.feature_geometry.x_start,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_end',
      label: 'X End',
      value: weldJSON.feature_geometry.x_end,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const ConeCircumferentialWeldJSONFromFormFields = (
  formFields: FormField[]
) => {
  return generateConeCircumferentialWeld(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x_start')?.value as number,
      GeckoMeasurementUnit.Degrees
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x_end')?.value as number,
      GeckoMeasurementUnit.Degrees
    )
  )
}

export const ConeCircumferentialWeldFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ConeCircumferentialWeld,
  title: 'Circumferential Welds',
  singularName: 'Circumferential Weld',
  fields: ['x_start', 'x_end', 'y'],
  sortBy: [{ fieldName: 'y', order: 'asc' }],
  disableEditing: true,
  tableMessage:
    'Adding / Editing of Circumferential Welds is disabled as they are automatically generated from Plates and/or Courses',
}
